/**
 * Laying out a text layer, once.
 *
 * Shaping is the most expensive thing the editor does, and three separate
 * places want the same answer: the canvas drawing the glyphs, the caret
 * working out where to sit, and a click working out which character it landed
 * on. Shaping three times would be three times the cost and, worse, three
 * chances to disagree about where a letter is.
 *
 * So it happens here and is cached on the inputs that affect it. The cache is
 * small and bounded: a card has a handful of text layers, and a layer that is
 * being typed into replaces its own entry on every keystroke.
 */

import type { TextLayer } from '../model/types';
import { ptToMm } from '../model/units';
import { shaperFor } from './fonts';
import type { ShapedParagraph } from './shaper';
import { caretX, indexAt, lineOf, selectionBoxes, snapIndex } from './carets';
import { isBent } from './place';

export interface Laid {
  paragraph: ShapedParagraph;
  /** Font units per millimetre, which is the only conversion anyone needs. */
  unitsPerMm: number;
}

/** Enough entries for a busy card, and few enough to never think about. */
const LIMIT = 64;

const cache = new Map<string, Laid>();

/** Everything about a layer that changes its layout, and nothing that does not. */
function signature(layer: TextLayer): string {
  return [
    layer.text,
    isBent(layer) ? 'bent' : layer.w,
    layer.align,
    layer.direction,
    layer.lineHeight,
    layer.letterSpacing,
    layer.font.family,
    layer.font.weight,
    layer.font.style,
    layer.font.size,
  ].join('');
}

/**
 * The shaped paragraph for a layer, or null while its font is still loading.
 *
 * Null is not a failure - it means "not yet". The canvas draws nothing for that
 * layer rather than falling back to another font, because a customer who sees
 * Arial for a moment will assume the card prints in Arial.
 */
export function layoutFor(layer: TextLayer): Laid | null {
  if (layer.text === '') return null;

  const key = signature(layer);
  const hit = cache.get(key);

  if (hit) return hit;

  const shaper = shaperFor(layer.font);

  if (!shaper) return null;

  const unitsPerMm = shaper.unitsPerEm / ptToMm(layer.font.size);

  const laid: Laid = {
    unitsPerMm,
    paragraph: shaper.layout(layer.text, {
      width: wrapWidth(layer) * unitsPerMm,
      align: layer.align,
      lineHeight: layer.lineHeight,
      letterSpacing: layer.letterSpacing * unitsPerMm,
      direction: layer.direction,
    }),
  };

  if (cache.size >= LIMIT) {
    // Oldest first. Map preserves insertion order, which is all this needs.
    const oldest = cache.keys().next().value;

    if (oldest !== undefined) cache.delete(oldest);
  }

  cache.set(key, laid);

  return laid;
}

/**
 * Where a text wraps, in millimetres.
 *
 * Bent text does not wrap: an arc or a drawn line is one direction to read in,
 * and the box around bent text is fitted to the bend rather than being a
 * column to fill - so wrapping at the box's width would feed the box's size
 * back into the text that sets it. Lines still break where the customer
 * pressed Enter. Shared with the press walk, which must wrap identically.
 */
export function wrapWidth(layer: TextLayer): number {
  if (isBent(layer)) return Infinity;

  return layer.w > 0 ? layer.w : Infinity;
}

/** Throws the cache away when a font arrives, so unshaped layers try again. */
export function forgetLayouts(): void {
  cache.clear();
}

/**
 * Which character a point inside a text layer lands on.
 *
 * @param at Position inside the layer, in millimetres from its top-left.
 * @returns A character offset, or null when the layer is not laid out yet.
 */
export function caretAt(layer: TextLayer, at: { x: number; y: number }): number | null {
  const laid = layoutFor(layer);

  if (!laid) return null;

  const { paragraph, unitsPerMm } = laid;

  // Lines are one leading apart, measured down from the top of the box.
  const leadingMm = paragraph.leading / unitsPerMm;
  const index = Math.floor(at.y / Math.max(0.001, leadingMm));
  const line = paragraph.lines[Math.max(0, Math.min(paragraph.lines.length - 1, index))];

  if (!line) return null;

  return indexAt(line, at.x * unitsPerMm);
}

/**
 * The nearest character position a caret may actually occupy.
 *
 * Characters that shape together are one cluster, and a caret cannot sit
 * inside one. Pressing an arrow key moves the textarea's own caret by a code
 * unit, which may land inside a cluster; this is what steps it the rest of the
 * way, so the caret goes over the pair rather than disappearing into it.
 *
 * @param prefer Direction of travel, or 'near' for a click.
 */
export function snapCaret(
  layer: TextLayer,
  index: number,
  prefer: 'forward' | 'back' | 'near' = 'near'
): number {
  const laid = layoutFor(layer);

  if (!laid) return index;

  const line = laid.paragraph.lines[lineOf(laid.paragraph, index)];

  return line ? snapIndex(line, index, prefer) : index;
}

/**
 * Where the caret sits, in millimetres from the layer's top-left.
 *
 * @returns x, the top of the caret, and its height.
 */
export function caretRect(
  layer: TextLayer,
  index: number
): { x: number; y: number; height: number } | null {
  const laid = layoutFor(layer);

  if (!laid) return null;

  const { paragraph, unitsPerMm } = laid;
  const which = lineOf(paragraph, index);
  const line = paragraph.lines[which];

  if (!line) return null;

  return {
    x: caretX(line, index) / unitsPerMm,
    y: (which * paragraph.leading) / unitsPerMm,
    // The full line box, not just the letters, so the caret does not shrink on
    // a line that happens to have no ascenders in it.
    height: (paragraph.ascender - paragraph.descender) / unitsPerMm,
  };
}

/**
 * The highlight behind a selected range, in millimetres.
 *
 * One box per stretch of adjacent glyphs per line, so a selection that crosses
 * a direction change comes back as two boxes rather than one that spans the
 * gap between them.
 */
export function selectionRects(
  layer: TextLayer,
  from: number,
  to: number
): Array<{ x: number; y: number; w: number; h: number }> {
  const laid = layoutFor(layer);

  if (!laid || from === to) return [];

  const { paragraph, unitsPerMm } = laid;
  const out: Array<{ x: number; y: number; w: number; h: number }> = [];
  const height = (paragraph.ascender - paragraph.descender) / unitsPerMm;

  paragraph.lines.forEach((line, which) => {
    for (const box of selectionBoxes(line, from, to)) {
      out.push({
        x: box.x / unitsPerMm,
        y: (which * paragraph.leading) / unitsPerMm,
        w: box.width / unitsPerMm,
        h: height,
      });
    }
  });

  return out;
}
